/**
 * How a file opens when someone double-clicks it — "the preferred way".
 * Pure: no React.
 *
 * Every file gets a PREVIEW where the browser can show one (an image in a
 * viewer window, a PDF rendered, a Word document converted to readable text,
 * audio / video with controls), and a list of ARCHES that can open it for
 * real — "Open in Image Editor".
 *
 * An arche is offered only when it can actually receive the file. Two kinds:
 *   - internal arches accept the `open_document` handoff in the platform's
 *     intake registry (`ARCHE_HANDOFF_INTAKE`); the candidates below are the
 *     ones each kind SHOULD open in, so one appears here as soon as it ships
 *     its intake;
 *   - plugin apps (WordFlow, SheetFlow, …) come from the server's
 *     file-intake list (ADR-030) — the kinds an admin approved, still
 *     declared by the plugin — passed in as `pluginIntakes`
 *     (`useFileOpeners` binds the current list).
 *
 * `preferApp`: the double-click goes straight to the first available arche
 * (a Word document belongs in a word processor), falling back to the
 * preview when none is available.
 */
import { ARCHE_HANDOFF_INTAKE } from '@/components/fias-ai/handoff';
import { fileMatchesIntake } from '@/components/fias-ai/plugin-intake-snapshot';
import { mimeToFileKind, type VaultFileKind } from '@/lib/vault-file-kinds';
import { previewTypeFor } from './file-display';

export type FilePreview = 'image' | 'pdf' | 'document' | 'text' | 'audio' | 'video';

export interface FileApp {
  archeId: string;
  /** "Image Editor" — shown as "Open in Image Editor". */
  label: string;
}

export interface FileOpener {
  preview: FilePreview | null;
  apps: FileApp[];
  /** Open in `apps[0]` on double-click instead of the preview. */
  preferApp: boolean;
}

/**
 * The INTERNAL arches each kind should open in, best first. `arche_pdf_studio`
 * is the PDF editor (being converted to a plugin; not in the intake registry
 * yet). Plugin apps are never listed here — their ids are per-environment;
 * they arrive through `pluginIntakes`.
 */
export const OPENER_CANDIDATES: Partial<Record<VaultFileKind, readonly string[]>> = {
  image: ['arche_image_editor'],
  pdf: ['arche_pdf_studio'],
};

/** Kinds whose natural home is an arche, not a preview. */
const PREFER_APP: ReadonlySet<VaultFileKind> = new Set(['document', 'spreadsheet', 'presentation']);

/** MIME types a Word-document preview can convert (mammoth reads .docx). */
const DOCX_MIME = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

function previewFor(mime: string | null | undefined, name: string): FilePreview | null {
  if (mime === DOCX_MIME || /\.docx$/i.test(name)) return 'document';
  const type = previewTypeFor(mime);
  return type === 'unsupported' ? null : type;
}

/** Can this arche receive a file right now (an `open_document` intake)? */
export function acceptsFiles(archeId: string): boolean {
  return ARCHE_HANDOFF_INTAKE[archeId]?.kinds.includes('open_document') ?? false;
}

/** A plugin app the server lists as able to open files (ADR-030). */
export interface PluginFileIntake {
  archeId: string;
  label: string;
  kinds: readonly string[];
  /** null = whole kinds; otherwise the file's extension or MIME type must be listed. */
  types?: readonly string[] | null;
}

export function openerFor(
  file: { mimeType: string | null; name: string },
  pluginIntakes: readonly PluginFileIntake[] = [],
): FileOpener {
  const kind = mimeToFileKind(file.mimeType);
  const internal = (OPENER_CANDIDATES[kind] ?? []).filter(acceptsFiles).map((archeId) => ({
    archeId,
    label: ARCHE_HANDOFF_INTAKE[archeId]!.label,
  }));
  const plugins = pluginIntakes
    .filter((p) => fileMatchesIntake(file, p))
    .map((p) => ({ archeId: p.archeId, label: p.label }))
    .sort((x, y) => x.label.localeCompare(y.label));
  const apps = [...internal, ...plugins];
  return {
    preview: previewFor(file.mimeType, file.name),
    apps,
    preferApp: PREFER_APP.has(kind) && apps.length > 0,
  };
}

/** What a double-click does. */
export function doubleClickAction(
  file: {
    mimeType: string | null;
    name: string;
  },
  pluginIntakes: readonly PluginFileIntake[] = [],
): { kind: 'app'; app: FileApp } | { kind: 'preview' } | { kind: 'download' } {
  const opener = openerFor(file, pluginIntakes);
  if (opener.preferApp) return { kind: 'app', app: opener.apps[0] };
  if (opener.preview) return { kind: 'preview' };
  if (opener.apps.length > 0) return { kind: 'app', app: opener.apps[0] };
  return { kind: 'download' };
}
